/**
 * Avisos de una plantilla de correo: cosas que **no** impiden guardarla pero que
 * salen mal en el correo del deudor.
 *
 * Vive en `shared` para que los vea quien escribe **mientras escribe**, no al
 * revisar el envío. Los tres salieron de un correo real del 27/10/2026, y
 * ninguno daba error en ninguna parte: el correo salió, y salió mal.
 *
 * No son reglas de validación: una plantilla con avisos se guarda igual. Hay
 * casos legítimos para cada uno —una divisa escrita a mano, un enlace de un
 * proveedor del cliente— y bloquearlos sería adivinar.
 */

/**
 * Dominios que no deberían aparecer en un correo de AR.
 *
 * El correo sale del subdominio del cliente y **ClaimUp no aparece en ninguna
 * parte**: es la premisa del canal. Un enlace de seguimiento de SendGrid metía
 * nuestra infraestructura en el correo del cliente, y encima era de una campaña
 * vieja. Pasa al copiar el texto de un correo **ya enviado**, donde el enlace ya
 * estaba reescrito por el proveedor.
 */
const DOMINIOS_QUE_NO_PINTAN_NADA = [
  'sendgrid.net',
  'sendgrid.com',
  'ct.sendgrid.net',
  'mailchimp.com',
  'list-manage.com',
  'hubspotlinks.com',
  'mandrillapp.com',
];

/** Símbolos de divisa. Si uno está pegado a `{{amount}}`, se duplica. */
const SIMBOLOS = ['€', '$', '£', 'EUR', 'USD', 'GBP'];

function texto(partes) {
  return [partes?.subject, partes?.body_text].filter(Boolean).join('\n');
}

/**
 * ¿Hay un símbolo de divisa pegado a `{{amount}}`?
 *
 * `{{amount}}` ya trae el símbolo, así que «de € {{amount}}» sale como
 * «de € 123,45 €». Pasó en el primer correo real del canal.
 */
function simboloPegadoAlImporte(t) {
  for (const s of SIMBOLOS) {
    // ⚠️ El símbolo va escapado: `$` es un metacarácter y sin escapar convierte
    // el patrón en un ancla de fin de cadena. Sin esto, «por importe de
    // {{amount}}» disparaba el aviso del dólar — lo cazó su test.
    const sim = s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Antes o después, con hasta dos espacios en medio.
    const antes = new RegExp(`${sim}\\s{0,2}\\{\\{\\s*amount\\s*\\}\\}`);
    const despues = new RegExp(`\\{\\{\\s*amount\\s*\\}\\}\\s{0,2}${sim}`);
    if (antes.test(t) || despues.test(t)) return s;
  }
  return null;
}

/**
 * Los dominios ajenos que aparecen en el texto.
 *
 * Sin los que son sufijo de otro que también sale: `ct.sendgrid.net` y
 * `sendgrid.net` son el mismo enlace, y nombrar los dos en el aviso lo hace
 * parecer dos problemas.
 */
function dominiosAjenos(t) {
  const encontrados = DOMINIOS_QUE_NO_PINTAN_NADA.filter((d) => t.includes(d));
  return encontrados.filter(
    (d) => !encontrados.some((otro) => otro !== d && otro.endsWith(`.${d}`))
  );
}

/**
 * @param {{subject?: string, body_text?: string}} partes
 * @returns {string[]} los avisos, en español y listos para pintar
 */
function avisosDePlantilla(partes) {
  const t = texto(partes);
  const avisos = [];

  const simbolo = simboloPegadoAlImporte(t);
  if (simbolo) {
    avisos.push(
      `Tienes «${simbolo}» pegado a {{amount}}, y esa variable ya trae el símbolo: ` +
        `al deudor le llegará dos veces. Quita el símbolo, o usa {{amount_number}}, que va sin él.`
    );
  }

  // El enlace de pago es el **del expediente**, no uno fijo de la plantilla, y
  // no todos los expedientes tienen uno: hay clientes enteros al 0%. Quien
  // escribe tiene que saber que a esos deudores les llegará el correo **sin esa
  // línea**, no un enlace roto ni un correo sin salir.
  if (/\{\{\s*payment_url\s*\}\}/.test(t)) {
    avisos.push(
      'Has puesto {{payment_url}}: es el enlace de pago de CADA expediente, no uno fijo. ' +
        'A los expedientes que no tengan enlace les llegará este correo sin la línea donde lo ' +
        'has puesto, así que escríbela de forma que se pueda caer sola.'
    );
  }

  const ajenos = dominiosAjenos(t);
  if (ajenos.length) {
    avisos.push(
      `Hay un enlace a ${ajenos.join(', ')} en el texto. Eso suele venir de copiar un correo ` +
        `ya enviado, y mete la infraestructura de otro proveedor en un correo que sale desde tu ` +
        `dominio. Pega la dirección de destino a mano.`
    );
  }

  return avisos;
}

module.exports = {
  avisosDePlantilla,
  simboloPegadoAlImporte,
  dominiosAjenos,
  DOMINIOS_QUE_NO_PINTAN_NADA,
};
